import React from 'react';
import { Typography } from '@mui/material';

import { Loader } from 'BMapsSrc/Loader';
import { Slider } from 'BMapsUI/UIComponents';
import BMapsTooltip from 'BMapsUI/BMapsTooltip';
import TooltipInfo from 'BMapsUI/common/TooltipInfo';
import { BestPoseBindingInfo, poseExchemP } from 'BMapsModel/FragmentSearchResult';
import { Length3, Sub3_3 } from 'BMapsSrc/math';
import { conditionalObjectItem } from 'BMapsSrc/util/js_utils';
import { isGroupEntry } from 'BMapsRedux/projectState/entry';
import { LazyCoralComponent, CORAL_ORGANISM_MISMATCH_COLOR } from './CoralComponent';
import { finiteStats } from './pose_stats';
import LinkLikeButton from '../../common/LinkLikeButton';
import { AnnotatedSearchResult } from './AnnotatedSearchResult';
import { svgImgSrc } from '../../ui_utils';
import {
    renderDebug, getMapCaseAndTargetInfo, getKinaseIdForMapCase, targetsHaveMixedOrganisms,
} from './utils';
import { isCrossOrganism } from './organism';
import {
    getColumnInfo, getValue,
    ActiveTargetColor, ActiveOffTargetColor,
} from './column_definitions';

// Approximate widths (px) used to stack sticky-right columns.
// These columns only appear in multi-protein / kinase mode, so offsets are
// computed at render time from whichever columns are actually present.
const STICKY_RIGHT_APPROX_WIDTH = { coralMap: 100, selectivityScore: 85, mwt: 110 };
const APPROX_CONTROLS_WIDTH = 80;
const STICKY_POPUP_BG = 'var(--popup-bg-color)';
const STICKY_CONTROLS_STYLE = {
    position: 'sticky',
    right: 0,
    zIndex: 2,
    backgroundColor: STICKY_POPUP_BG,
    whiteSpace: 'nowrap',
    minWidth: APPROX_CONTROLS_WIDTH,
};

function computeStickyRightStyles(columns) {
    const styles = {};
    let rightOffset = APPROX_CONTROLS_WIDTH;
    for (let i = columns.length - 1; i >= 0; i--) {
        const baseCol = columns[i].split('+')[0];
        const colWidth = STICKY_RIGHT_APPROX_WIDTH[baseCol];
        if (colWidth) {
            styles[columns[i]] = {
                position: 'sticky',
                right: rightOffset,
                zIndex: 2,
                backgroundColor: STICKY_POPUP_BG,
                whiteSpace: 'nowrap',
                minWidth: colWidth,
            };
            rightOffset += colWidth;
        }
    }
    return styles;
}

/**
 * Component to display a fragment search result table with header and data rows.
 */
export function SuggestionTable({
    uiInfo, sortedSuggestions, columns,
    sortCol, sortAscending, handleSort,
    showImages, toggleImages,
    suggestionAccept, suggestionEnter, suggestionExit,
    cycleSuggestions,
    isPinned, togglePin,
    activityInfo, projectCases, forceProteinLabel,
    sortPosesBy, handleSortPosesBy, allDisplayFilters, handleUpdateDisplayFilters,
    globalFilter, anyValidBindingScore, primaryScoreField, searchInfo,
}) {
    const stickyRightStyles = computeStickyRightStyles(columns);
    return (
        <table>
            <thead>
                <SuggestionsHeader
                    columns={columns}
                    sortCol={sortCol}
                    sortAscending={sortAscending}
                    handleSort={handleSort}
                    showImages={showImages}
                    toggleImages={toggleImages}
                    forceProteinLabel={forceProteinLabel}
                    stickyRightStyles={stickyRightStyles}
                />
            </thead>
            <tbody>
                { sortedSuggestions.map((d) => (
                    <SuggestionRow
                        key={d.index}
                        primaryScoreField={primaryScoreField}
                        searchInfo={searchInfo}
                        sugg={d}
                        uiInfo={uiInfo}
                        columns={columns}
                        showImg={showImages}
                        suggestionAccept={suggestionAccept}
                        suggestionEnter={suggestionEnter}
                        suggestionExit={suggestionExit}
                        onChangePose={cycleSuggestions}
                        pinned={isPinned(d)}
                        togglePin={togglePin}
                        projectCases={projectCases}
                        forceProteinLabel={forceProteinLabel}
                        activityInfo={activityInfo}
                        sortPosesBy={sortPosesBy}
                        handleSortPosesBy={handleSortPosesBy}
                        displayFilters={allDisplayFilters[d.name]}
                        handleUpdateDisplayFilters={handleUpdateDisplayFilters}
                        globalFilter={globalFilter}
                        stickyRightStyles={stickyRightStyles}
                        anyValidBindingScore={anyValidBindingScore}
                    />
                ))}
            </tbody>
        </table>
    );
}
/**
 * Component to display one row of a fragment search result table.
 * @description props = {
 *      sugg: suggestion object,
 *      showImg: whether to show images instead of fragment names,
 *      uiInfo: labels and presence of certain controls, based on command
 *      suggestionAccept, suggestionEnter, suggestionAccept: callbacks
 * }
 */
class SuggestionRow extends React.PureComponent {
    constructor(props) {
        super(props);
        this.state = { sliderExpanded: false };

        this.toggle = this.toggle.bind(this);
        this.onChange = this.onChange.bind(this);
    }

    onChange(valueIn) {
        const { sugg, onChangePose } = this.props;
        const value = Number(valueIn);
        onChangePose(sugg, value);
        this.forceUpdate();
    }

    getEltId() {
        const { sugg } = this.props;
        return `suggestion-${sugg.index}`;
    }

    toggle() {
        this.setState((old) => {
            const { sugg, suggestionExit } = this.props;
            if (old.sliderExpanded) suggestionExit(sugg);
            return { sliderExpanded: !old.sliderExpanded };
        });
    }

    render() {
        if (renderDebug) console.log(`Rendering SuggestionRow ${this.getEltId()}`);
        const {
            sugg, showImg, uiInfo, columns,
            suggestionAccept, suggestionEnter, suggestionExit,
            pinned, togglePin,
            activityInfo, projectCases, forceProteinLabel,
            sortPosesBy, handleSortPosesBy, displayFilters, handleUpdateDisplayFilters,
            stickyRightStyles,
            anyValidBindingScore, primaryScoreField, searchInfo,
        } = this.props;
        const { sliderExpanded } = this.state;

        const eltId = this.getEltId();
        const altText = `2D diagram of ${sugg.name}`;
        const imgTooltip = (<img alt={altText} className="suggestion-imgTooltip" src={svgImgSrc(sugg.img)} />);
        const tooltip = showImg ? sugg.name : imgTooltip;

        const addOnRowClick = !uiInfo.usingSlider;
        const accept = () => suggestionAccept(sugg);
        const addButton = (
            <BMapsTooltip
                placement="top"
                title={sugg.selectedFrag ? uiInfo.addButtonTitle : 'No poses available. Clear filters to see more fragments.'}
            >
                <LinkLikeButton
                    style={{ color: 'var(--marketing-blue)' }}
                    onClick={(event) => { event.stopPropagation(); accept(); }}
                    disabled={!sugg.selectedFrag}
                >
                    <i className="fa fa-plus-square-o" />
                </LinkLikeButton>
            </BMapsTooltip>
        );
        const pin = (
            <BMapsTooltip
                placement="top"
                title={sugg.displayPoses.length > 1
                    ? 'Keep viewing these poses in the workspace (hover previews up to 500 poses; pin to view all)'
                    : 'Keep viewing this pose in the workspace'}
            >
                <LinkLikeButton
                    onClick={(event) => { event.stopPropagation(); togglePin(sugg); }}
                    style={{ color: pinned ? 'var(--marketing-blue)' : 'lightgrey' }}
                >
                    <i className="fa fa-thumb-tack" />
                </LinkLikeButton>
            </BMapsTooltip>
        );
        // Pose count for the toggle: post-global-filter count if the
        // global primary-score slider is in play, falling back to total
        // frags otherwise. Per-row pose filters do NOT enter this count —
        // they only affect what the expanded pose explorer renders.
        const togglePoseCount = sugg.globalFilteredCount ?? sugg.frags.length;
        const togglePoseCountText = `${togglePoseCount}\xa0${togglePoseCount === 1 ? 'pose' : 'poses'}\xa0available`;
        const toggleButton = (
            <BMapsTooltip
                placement="right"
                // \xa0 is non-breaking space
                title={sliderExpanded ? `Hide pose explorer (${togglePoseCountText})` : `Show pose explorer (${togglePoseCountText})`}
                // tooltip can interfere with dropdown button, so move it right a little.
                slotProps={{
                    popper: {
                        modifiers: [
                            {
                                name: 'offset',
                                options: {
                                    offset: [0, 6],
                                },
                            },
                        ],
                    },
                }}
            >
                <LinkLikeButton
                    onClick={this.toggle}
                    className="toggle"
                    style={{ color: 'var(--marketing-blue)' }}
                >
                    {sliderExpanded
                        ? <i className="fa fa-caret-down" />
                        : <i className="fa fa-caret-left" /> }
                    {' '}
                    <span style={{ fontSize: '0.85em' }}>{togglePoseCount}</span>
                </LinkLikeButton>
            </BMapsTooltip>
        );

        return (
            <>
                <tr
                    key={eltId}
                    className="suggestionRow"
                    id={eltId}
                    onMouseEnter={() => suggestionEnter(sugg)}
                    onMouseLeave={() => suggestionExit(sugg)}
                    onClick={() => addOnRowClick && accept()}
                >
                    { columns.map((col, i) => (
                        <SuggestionRowCell
                            key={`${eltId}+${col}`}
                            suggestion={sugg}
                            col={col}
                            index={i}
                            activityInfo={activityInfo}
                            tooltip={col === 'name' || col === 'svg' ? tooltip : undefined}
                            stickyStyle={stickyRightStyles?.[col]}
                            primaryScoreField={primaryScoreField}
                            searchInfo={searchInfo}
                            suggestionEnter={suggestionEnter}
                            suggestionExit={suggestionExit}
                        />
                    ))}
                    <td style={STICKY_CONTROLS_STYLE}>
                        {pin}
                        {addButton}
                        {uiInfo.usingSlider && toggleButton}
                    </td>
                </tr>
                {uiInfo.usingSlider && (
                <tr
                    key={`slider-${sugg.index}`}
                    onMouseEnter={() => suggestionEnter(sugg)}
                    onMouseLeave={() => suggestionExit(sugg)}
                >
                    <td colSpan="100" style={{ position: 'sticky', left: 0, backgroundColor: 'var(--popup-bg-color)' }}>
                        {sliderExpanded && (
                            <div style={{ maxWidth: 'calc(100vw - 100px)' }}>
                                <SuggestionPoseDetail
                                    onChange={this.onChange}
                                    sugg={sugg}
                                    projectCases={projectCases}
                                    forceProteinLabel={forceProteinLabel}
                                    sortPosesBy={sortPosesBy}
                                    handleSortPosesBy={handleSortPosesBy}
                                    displayFilters={displayFilters}
                                    handleUpdateDisplayFilters={handleUpdateDisplayFilters}
                                    toggle={this.toggle}
                                    anyValidBindingScore={anyValidBindingScore}
                                    searchInfo={searchInfo}
                                />
                            </div>
                        )}
                    </td>
                </tr>
                )}
            </>
        );
    }
}

function SuggestionPoseDetail({
    onChange, sugg, projectCases, forceProteinLabel,
    sortPosesBy, handleSortPosesBy, displayFilters, handleUpdateDisplayFilters,
    toggle, anyValidBindingScore, searchInfo,
}) {
    const { referenceOrganism='' } = searchInfo;
    const sliderValue = sugg.selectedDisplayPose?.displayIndex ?? -1;
    const poses = sugg.displayPoses;
    const currentPose = sugg.getFragByDisplayIndex(sliderValue);
    // Cross-organism flag: the displayed pose's structure organism vs the search's source.
    // Fail-open -- no reference or no organism => no flag.
    const structTitle = structureTooltipTitle(currentPose?.projectCase) || '';
    const poseMapCase = getMapCaseAndTargetInfo(currentPose?.projectCase)?.mapCase;
    const poseOrganism = poseMapCase?.organism || '';
    const organismMismatch = isCrossOrganism(poseOrganism, referenceOrganism);
    // structTitle already names a non-human organism (structureTooltipTitle), so the
    // mismatch flag only adds the "differs from source" note.
    const structTooltip = organismMismatch
        ? `${structTitle} (differs from source ${referenceOrganism})`
        : structTitle;
    const structCellStyle = organismMismatch
        ? { color: CORAL_ORGANISM_MISMATCH_COLOR, fontWeight: 600 } : undefined;
    const filterValue = (propInfo) => displayFilters?.[propInfo.id]?.value ?? '';
    const setFilterValue = (propInfo, value) => {
        let entry;
        if (numberOrTruthy(value)) {
            const numericValue = Number(value);
            entry = {
                value: propInfo.precision != null ? numericValue : value,
                filter: propInfo.precision != null
                    ? (frag) => {
                        // Round pose value so if the user enters the visible min, it always passes.
                        const poseValue = posePropValue(sugg, frag, propInfo);
                        const factor = 10 ** propInfo.precision;
                        const roundedPoseValue = Math.round(poseValue * factor) / factor;
                        return roundedPoseValue <= numericValue;
                    }
                    : (frag) => posePropValue(sugg, frag, propInfo) === value,
            };
        }
        handleUpdateDisplayFilters({ [propInfo.id]: entry }, { sugg });
    };
    function clearFilters() {
        const filterObj = { };
        for (const prop of posePropsOfInterest()) {
            filterObj[prop.id] = null;
        }
        handleUpdateDisplayFilters(filterObj, { sugg });
    }
    const activeFilters = Object.values(displayFilters || {})
        .filter((filter) => numberOrTruthy(filter.value)).length;

    // Raw pose count for the suggestion (pre-any-filter), and the
    // post-global-filter count (which equals the raw count when the global
    // filter is inactive — defensive fallback to raw frags if a suggestion
    // was built without a global filter pass).
    const rawPoseCount = sugg.frags.length;
    const globalPoseCount = sugg.globalFilteredCount ?? rawPoseCount;
    const globalFilterActive = globalPoseCount < rawPoseCount;
    const visiblePoseCount = poses.length;
    let poseLabel = <i>{`No poses match the specified filter${activeFilters > 1 ? 's' : ''} (${rawPoseCount} total poses available)`}</i>;
    if (visiblePoseCount > 0) {
        // \xa0 is non-breaking space
        poseLabel = `Highlighting pose\xa0${1 + sliderValue}\xa0/\xa0${visiblePoseCount}`;
        if (visiblePoseCount !== rawPoseCount) {
            let suffix = `${rawPoseCount} total`;
            // Only annotate "passing global filter" when a per-row filter
            // has reduced the count below the post-global count — otherwise
            // the global count equals the visible count and the suffix just
            // repeats the number to its left.
            if (globalFilterActive && visiblePoseCount < globalPoseCount) {
                suffix += `, ${globalPoseCount} passing global filter`;
            }
            poseLabel += ` (filtered from ${suffix})`;
        }
    }
    // forceProteinLabel: clicked-on structure has no fragment data, so
    // results are from other protein(s) — show the Structure label even
    // though there is only one result protein.
    const multiProtein = projectCases.length > 1 || forceProteinLabel;

    return (
        <Typography
            component="div"
            className="suggestionPoseDetail"
        >
            <BMapsTooltip title="Hide pose explorer" placement="right">
                <LinkLikeButton
                    className="ligand_mod_close"
                    onClick={toggle}
                    style={{ color: 'black', padding: 0, lineHeight: 0 }}
                >
                    <i className="fa fa-close" />
                </LinkLikeButton>
            </BMapsTooltip>
            <div>{poseLabel}</div>
            {visiblePoseCount > 1 && (
                <div className="suggestionPoseDetailHeader">
                    <Slider
                        eltId={`suggestion-${sugg.index}-slider`}
                        value={sliderValue}
                        max={poses.length-1}
                        onChange={onChange}
                    />
                    Sort:
                    <select
                        value={sortPosesBy}
                        onChange={(e) => handleSortPosesBy(e.target.value, { sugg })}
                        style={{ fontSize: 'inherit' }}
                    >
                        {
                            posePropsOfInterest()
                                .filter((propInfo) => !propInfo.excludeFromExplorerSort
                                    && (multiProtein || !propInfo.multiProteinOnly)
                                    // Hide binding-score / exchemP sort options when no pose has a
                                    // value (every pose gen2).
                                    && !(['bindingScore', 'exchemPotential'].includes(propInfo.id)
                                        && !anyValidBindingScore))
                                .map((propInfo) => (
                                    <option key={propInfo.id} value={propInfo.id}>
                                        {propInfo.selectLabel || propInfo.label}
                                    </option>
                                ))
                        }
                    </select>
                    <TooltipInfo
                        placement="right"
                        title="Use the slider to highlight and view properties for specific poses in the visible distribution, which is organized according to the specified sort and filters."
                        sx={{ fontSize: '1rem' }}
                    />
                </div>
            )}
            <table style={{ marginBottom: 0 }}>
                <thead>
                    <tr>
                        <th style={{ textAlign: 'left' }}>
                            <BMapsTooltip
                                title="Properties of poses in the visible distribution for this fragment"
                                placement="top"
                            >
                                Property
                            </BMapsTooltip>
                        </th>
                        <th>
                            <BMapsTooltip
                                title="Property value for the highlighted pose"
                                placement="top"
                            >
                                Value
                            </BMapsTooltip>
                        </th>
                        <th>
                            <BMapsTooltip
                                title="Minimum property value across the *visible* distribution of poses for this fragment"
                                placement="top"
                            >
                                Min
                            </BMapsTooltip>
                        </th>
                        <th>
                            <BMapsTooltip
                                title="Average property value across the *visible* distribution of poses for this fragment"
                                placement="top"
                            >
                                Mean
                            </BMapsTooltip>
                        </th>
                        <th>
                            <BMapsTooltip
                                title="Maximum property value across the *visible* distribution of poses for this fragment"
                                placement="top"
                            >
                                Max
                            </BMapsTooltip>
                        </th>
                        <th>
                            <BMapsTooltip
                                title="Filter the visible distribution of poses for this fragment. Numeric filters specify the inclusive maximum."
                                placement="top"
                            >
                                Filter (Max)
                            </BMapsTooltip>
                            <BMapsTooltip placement="right" title="Remove all filters">
                                <LinkLikeButton
                                    onClick={() => clearFilters()}
                                    style={{
                                        visibility: activeFilters > 0 ? 'visible' : 'hidden',
                                        color: 'var(--marketing-blue)',
                                    }}
                                >
                                    <i className="fa fa-times" />
                                </LinkLikeButton>
                            </BMapsTooltip>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {getPoseDetails(sugg, sliderValue)
                        .filter(([prop]) => multiProtein || !prop.multiProteinOnly)
                        .filter(([prop, value]) => (
                            numberOrTruthy(value)
                            || numberOrTruthy(displayFilters?.[prop.id]?.value)
                        ))
                        .map(
                            ([prop, value, stats]) => (
                                <tr key={prop.id}>
                                    <td>
                                        <div style={{ justifyContent: 'start' }}>{prop.label}</div>
                                    </td>
                                    <td>
                                        {prop.id === 'projectCase' ? (
                                            // Same structure tooltip as the group header / filter;
                                            // tinted when the pose's organism differs from source.
                                            <BMapsTooltip placement="top" title={structTooltip}>
                                                <div style={structCellStyle}>{value ?? ''}</div>
                                            </BMapsTooltip>
                                        ) : (
                                            <div>{value ?? ''}</div>
                                        )}
                                    </td>
                                    <td>
                                        <div>{stats?.min?.toFixed(1) ?? ''}</div>
                                    </td>
                                    <td>
                                        <div>{stats?.avg?.toFixed(1) ?? ''}</div>
                                    </td>
                                    <td>
                                        <div>{stats?.max?.toFixed(1) ?? ''}</div>
                                    </td>
                                    <td>
                                        <div style={{ paddingLeft: '1.5em' }}>
                                            {getPosePropFilter(
                                                prop, sugg, filterValue(prop),
                                                setFilterValue, searchInfo,
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )
                        )}
                </tbody>
            </table>
        </Typography>
    );
}

/**
 * Component to display the header of a fragment search result table.
 */
function SuggestionsHeader({
    columns,
    sortCol, sortAscending, handleSort,
    toggleImages, forceProteinLabel,
    stickyRightStyles,
}) {
    return (
        <>
            {/* grouphead displays protein labels in multi-protein case, or
                when forced (e.g. clicked-on structure has no fragments so
                results come from a different protein). */}
            <SuggestionGroupHeadRow
                columns={columns}
                forceProteinLabel={forceProteinLabel}
            />
            {/* colhead displays the sortable column headers */}
            <tr key="header-row" className="ligandmod-colhead">
                {/* Frag/Name toggle column */}
                <BMapsTooltip placement="top" title="Switch between fragment images and names">
                    <th onClick={() => { toggleImages(); }}>
                        <i id="image-name-toggle" className="fa fa-random" />
                    </th>
                </BMapsTooltip>
                {/* Columns to display */}
                { columns.map((col) => (
                    <SuggestionColumnHead
                        key={col}
                        col={col}
                        sortCol={sortCol}
                        sortAscending={sortAscending}
                        handleSort={handleSort}
                        stickyStyle={stickyRightStyles?.[col]}
                    />
                ))}
                {/* Controls column */}
                <th aria-label="Controls" style={STICKY_CONTROLS_STYLE}> </th>
            </tr>
        </>
    );
}

/**
 * @param {AnnotatedSearchResult} sugg
 */
function getInfoForCoralMap(sugg) {
    return Object.values(sugg.perCaseInfo).map((projectCaseEntry) => ({
        kinaseId: getKinaseIdForMapCase(projectCaseEntry.mapCase),
        projectCase: projectCaseEntry.projectCase,
        mapCase: projectCaseEntry.mapCase,
        targetInfo: projectCaseEntry.targetInfo,
        fragInfo: sugg.fragInfo,
        bindingScore: projectCaseEntry.bestPoseInfo?.bindingScore,
        ligandEfficiency: projectCaseEntry.bestPoseInfo?.ligandEfficiency,
        enSolute: projectCaseEntry.bestPoseInfo?.enSolute,
        ligandEfficiencyEn: projectCaseEntry.bestPoseInfo?.ligandEfficiencyEn,
    }));
}

function SuggestionColumnHead({
    col, handleSort, sortCol, sortAscending, stickyStyle,
}) {
    const { properties: { noSort, sortKey, table } } = getColumnInfo(col) || {};
    const { header, title, suffix } = table || {};

    const sortIcon = (c) => {
        if (c === sortCol) {
            const className = sortAscending ? 'fa fa-caret-up' : 'fa fa-caret-down';
            return <i className={className} style={{ display: 'inline' }} />;
        } else {
            return '';
        }
    };

    return (
        <BMapsTooltip placement="top" title={title || ''}>
            <th
                onClick={!noSort ? () => handleSort(sortKey || col) : undefined}
                style={stickyStyle || undefined}
            >
                {header}
                {!noSort && (
                    <>
                        &nbsp;
                        {sortIcon(col)}
                    </>
                )}
                {!!suffix && suffix}
            </th>
        </BMapsTooltip>
    );
}

/**
 * Display the GroupHead header row for the SuggestionTable.
 * In the multi-protein case, display the project case names with tooltips with target info.
 */
function SuggestionGroupHeadRow({ columns, forceProteinLabel }) {
    // Organize columns by groups according to their associated project case, so it can
    // create one <th> with a colSpan for the whole group.
    // <th>s for groups associated with a projectCase will have tooltips with target info.
    // This is perhaps unnecessarily complex because it avoids making assumptions about
    // column structure. This means it could handle something like:
    //     Col1, Col2, Col3_pcA, Col3_pcB, Col4, Col5_pcA, Col6_pcA, Col5_pcB, Col6_pcB
    // Resulting groups:
    //     [Col1, Col2], [Col3_A], {Col3_B], [Col4], [Col5_A, Col6_A], [Col5_B, Col6_B]

    const getColumnGroupEntry = (projectCase, cols) => {
        const { mapCase } = getMapCaseAndTargetInfo(projectCase);
        return {
            shortName: mapCase?.getShortName(),
            title: structureTooltipTitle(projectCase),
            cols: [...cols], // Make a copy
        };
    };

    const columnGroups = [];
    const allProjectCases = new Set();
    let workingProjectCase;
    let workingColumnGroup = [];

    for (const col of columns) {
        const { projectCase } = getColumnInfo(col);
        if (projectCase) allProjectCases.add(projectCase);
        if (projectCase !== workingProjectCase) {
            columnGroups.push(getColumnGroupEntry(workingProjectCase, workingColumnGroup));
            workingColumnGroup = [];
            workingProjectCase = projectCase;
        }
        workingColumnGroup.push(col);
    }

    columnGroups.push(getColumnGroupEntry(workingProjectCase, workingColumnGroup));

    return (allProjectCases.size > 1 || forceProteinLabel) && (
        <tr className="ligandmod-grouphead">
            {/* Frag/Name */}
            <th colSpan="1"> </th>
            {/* Columns */}
            {columnGroups.map(({
                cols, shortName, title,
            }, i) => {
                // Last group with no protein name = spans the sticky-right data columns (mwt etc.)
                const isLastUnprojected = i > 0 && i === columnGroups.length - 1 && !shortName;
                return (
                    <BMapsTooltip key={`groupheadCol_${i.toString()}`} placement="top" title={title || ''}>
                        <th
                            colSpan={cols.length}
                            style={isLastUnprojected ? {
                                position: 'sticky',
                                right: APPROX_CONTROLS_WIDTH,
                                zIndex: 3,
                                backgroundColor: STICKY_POPUP_BG,
                            } : undefined}
                        >
                            {shortName || ' '}
                        </th>
                    </BMapsTooltip>
                );
            })}
            {/* Controls */}
            <th style={STICKY_CONTROLS_STYLE}> </th>
        </tr>
    );
}

function SuggestionRowCell({
    suggestion, col, index, activityInfo, tooltip, stickyStyle, primaryScoreField,
    searchInfo, suggestionEnter, suggestionExit,
}) {
    const colInfo = getColumnInfo(col);
    if (!colInfo) return null;
    const { projectCase } = colInfo;
    const isCoralMap = colInfo.column === 'coralMap';
    const { highlightActiveFrags, activityThresholdInfo: threshInfo } = activityInfo;

    const isFirstCol = index === 0;
    const colSpan = isFirstCol ? 2 : undefined;
    const defaultStyle = { textAlign: 'right', paddingRight: '20px' };
    const style = { ...defaultStyle };
    if (projectCase && highlightActiveFrags) {
        const perCaseInfo = suggestion.perCaseInfo[projectCase];
        if (perCaseInfo.isActiveOffTarget(threshInfo)) style.backgroundColor = ActiveOffTargetColor;
        if (perCaseInfo.isActiveTarget(threshInfo)) style.backgroundColor = ActiveTargetColor;
    }

    // Sticky cells must keep the row's active-target/off-target tint when present; fall back to
    // the popup bg (so scrolled content doesn't bleed through) only when there's no tint.
    const cellStyle = stickyStyle
        ? { ...style, ...stickyStyle, backgroundColor: style.backgroundColor || STICKY_POPUP_BG }
        : style;

    // The coral map carries its own (kinome) tooltip and shouldn't also act as a hover
    // target for the fragment cloud the way the rest of the row does. While the pointer is
    // over the coral cell, suppress the cloud (suggestionExit no-ops when the row is pinned,
    // so a pinned suggestion stays displayed) and restore it on leave back into the row.
    const coralHoverProps = isCoralMap
        ? {
            onMouseEnter: () => suggestionExit(suggestion),
            onMouseLeave: () => suggestionEnter(suggestion),
        }
        : undefined;

    return (
        <BMapsTooltip placement="top" title={tooltip}>
            <td style={cellStyle} colSpan={colSpan} {...coralHoverProps}>
                { getTableValue(suggestion, col, primaryScoreField, searchInfo) }
            </td>
        </BMapsTooltip>
    );
}

function getTableValue(suggestion, col, primaryScoreField, searchInfo) {
    const val = getValue(suggestion, col);
    const { column, properties: { table } } = getColumnInfo(col);
    const { precision, getValue: getMyValue } = table || {};
    switch (true) {
        case precision != null:
            return val?.toFixed(precision) ?? '';
        case getMyValue != null:
            return getMyValue(val);
        case column === 'coralMap':
            return (
                <LazyCoralComponent
                    coralMapInfoEntries={getInfoForCoralMap(suggestion)}
                    primaryScoreField={primaryScoreField}
                    searchInfo={searchInfo}
                />
            );
        case column === 'svg': {
            const altText = `2D diagram of ${suggestion.name}`;
            return <img alt={altText} width="70px" src={svgImgSrc(suggestion.img)} />;
        }
        default:
            return val;
    }
}

function getPoseDetails(sugg, displayIndex) {
    const currentPose = sugg.getFragByDisplayIndex(displayIndex);
    const poseStats = getPoseStats(sugg);
    const entries = posePropsOfInterest()
        .filter((propInfo) => !propInfo.excludeFromTable)
        .map((propInfo) => [
            propInfo,
            currentPose ? poseDisplayValue(sugg, currentPose, propInfo) : null,
            poseStats[propInfo.id],
        ]);
    // Sort the property listing so that projectCase (Structure) is always first
    entries.sort(([a], [b]) => {
        switch (true) {
            case a.id === 'projectCase' && b.id !== 'projectCase': return -1;
            case a.id !== 'projectCase' && b.id === 'projectCase': return 1;
            default: return 0;
        }
    });
    return entries;
}

function getPoseStats(sugg) {
    const poses = sugg.displayPoses.map((pose) => pose.frag);
    if (poses.length === 0) return {};

    return posePropsOfInterest().reduce((acc, nextPropInfo) => {
        const { id: propId, precision } = nextPropInfo;
        if (precision == null) return acc; // not a number, can't do stats
        // finiteStats skips null/undefined/NaN so they don't skew min/max/avg — e.g. gen2 poses
        // have a null binding score; counting them (as 0) would corrupt a mixed-set summary.
        acc[propId] = finiteStats(poses.map((pose) => posePropValue(sugg, pose, nextPropInfo)));
        return acc;
    }, {});
}

function posePropsOfInterest() {
    return Object.values({
        position: {
            id: 'position',
            label: 'Position',
            precision: 1,
            excludeFromTable: true,
            calcValue(sugg, pose) {
                let value;
                const refFrag = sugg.displayPoses[0]?.frag;
                if (refFrag) {
                    const dist = Length3(Sub3_3(refFrag.translation, pose.translation));
                    value = Math.sqrt(dist);
                    value = dist;
                }
                return value;
            },
        },
        enSolute: {
            id: 'enSolute',
            label: 'Interaction Energy',
            precision: 1,
        },
        bindingScore: {
            id: 'bindingScore',
            label: 'Binding Score',
            precision: 1,
            calcValue(_, pose) {
                // null for gen2 poses (poseExchemP) → blank in the pose explorer.
                return BestPoseBindingInfo.getBindingScore(poseExchemP(pose));
            },
        },
        enSolv: {
            id: 'enSolv',
            label: (
                <>
                    &Delta;&Delta;G
                    <sub>s</sub>
                </>
            ),
            selectLabel: <>&Delta;&Delta;Gs</>,
            precision: 1,
        },
        exchemPotential: {
            id: 'exchemPotential',
            label: 'Ex. Chem. Potential',
            precision: 1,
            calcValue(_, pose) {
                // null for gen2 poses → blank in the pose explorer (exchemP not meaningful).
                return poseExchemP(pose);
            },
        },
        ...conditionalObjectItem(Loader.AllowDevFeatures, 'poseName', {
            id: 'poseName',
            label: 'Pose Name',
            precision: null,
            // Sortable in the pose explorer (dev-only, via sortFrags 'poseName' case); the other
            // dev-only props below stay excludeFromExplorerSort (no meaningful order).
            calcValue(_, pose) {
                return pose.getSpec();
            },
        }),
        ...conditionalObjectItem(Loader.AllowDevFeatures, 'poseIndex', {
            id: 'poseId',
            label: 'Pose ID',
            precision: null,
            excludeFromExplorerSort: true,
            calcValue(_, pose) {
                return pose.poseSerialNo ?? pose.ordinal;
            },
        }),
        ...conditionalObjectItem(Loader.AllowDevFeatures, 'translation', {
            id: 'translation',
            label: 'Position (Å)',
            precision: null,
            excludeFromExplorerSort: true,
            calcValue(_, pose) {
                const [x, y, z] = pose.translation;
                return `${x.toFixed(2)}, ${y.toFixed(2)}, ${z.toFixed(2)}`;
            },
        }),
        projectCase: {
            id: 'projectCase',
            label: 'Structure',
            precision: null,
            multiProteinOnly: true,
            displayValue(value) {
                return projectCaseDisplay(value);
            },
            getFilterInput: (filterValue, setFilterValue, sugg, searchInfo={}) => (
                <select
                    value={filterValue}
                    onChange={(e) => setFilterValue(e.target.value)}
                >
                    <option value="">{filterValue !== '' ? 'All' : ''}</option>
                    { sugg.projectCases
                        .map((pc) => ({
                            pc,
                            count: sugg.frags.filter((frag) => frag.projectCase === pc).length,
                        }))
                        // Omit structures with no poses (keep the selected one so the dropdown
                        // never loses its current value).
                        .filter(({ pc, count }) => count > 0 || pc === filterValue)
                        .map(({ pc, count }) => {
                            // Leading markers (an <option> can't be color-tinted reliably across
                            // browsers, unlike the Structure value cell): '*' cross-organism,
                            // '†' off-tree (a lipid kinase with no coral-map node). The native
                            // title carries the notes (an <option> can't host a MUI tooltip).
                            const { referenceOrganism='' } = searchInfo;
                            const mapCase = getMapCaseAndTargetInfo(pc)?.mapCase;
                            const organism = mapCase?.organism || '';
                            const crossOrganism = isCrossOrganism(organism, referenceOrganism);
                            const offTree = !!mapCase?.otherProperties?.offTree;
                            let title = structureTooltipTitle(pc);
                            if (title) {
                                if (crossOrganism) title = `${title} (differs from source ${referenceOrganism})`;
                                if (offTree) title = `${title} — off-tree (no coral-map node)`;
                            }
                            const marker = `${crossOrganism ? '*' : ''}${offTree ? '†' : ''}`;
                            return (
                                <option key={pc} value={pc} title={title}>
                                    {`${marker}${projectCaseDisplay(pc)}`}
                                    {filterValue !== pc ? ` (${count})` : ''}
                                </option>
                            );
                        })}
                </select>
            ),
        },
    });
}

function posePropValue(sugg, pose, propInfo) {
    const { id: propId, calcValue } = propInfo;
    const value = calcValue ? calcValue(sugg, pose) : pose[propId];
    return value;
}

function poseDisplayValue(sugg, pose, propInfo) {
    const { displayValue, precision } = propInfo;
    const value = posePropValue(sugg, pose, propInfo);
    if (displayValue) return displayValue(value);
    return (precision == null ? value : value?.toFixed(precision));
}

function getPosePropFilter(propInfo, sugg, filterValue, setFilterValue, searchInfo) {
    const { getFilterInput } = propInfo;
    let input;
    if (getFilterInput) {
        input = getFilterInput(
            filterValue, (val) => setFilterValue(propInfo, val), sugg, searchInfo,
        );
    } else if (propInfo.precision != null) {
        input = (
            <input
                type="number"
                step="0.5"
                value={filterValue}
                onChange={(e) => setFilterValue(propInfo, e.target.value)}
            />
        );
    }

    return (!!input && (
        <>
            {input}
            <BMapsTooltip placement="right" title="Remove filter">
                <LinkLikeButton
                    onClick={() => setFilterValue(propInfo, null)}
                    style={{
                        visibility: numberOrTruthy(filterValue) ? 'visible' : 'hidden',
                        color: 'var(--marketing-blue)',
                    }}
                >
                    <i className="fa fa-times" />
                </LinkLikeButton>
            </BMapsTooltip>
        </>
    ));
}

function numberOrTruthy(value) {
    return typeof value === 'number' || !!value;
}

function projectCaseDisplay(projectCase) {
    const info = getMapCaseAndTargetInfo(projectCase);
    return info.mapCase.getShortName();
}

/**
 * Tooltip text identifying a structure (projectCase): "<long name> (Target)" / "<long name>
 * (Off-Target)". The Target/Off-Target suffix is included only for an INDIVIDUAL entry (a loaded
 * structure or a structure of interest) that has actually been assigned target/off-target status --
 * a curated-panel (group) member is unassigned by definition, so it gets just the long name, never
 * the suffix (isGroupEntry, matching AnnotatedSearchResult's own target/off-target gating). Returns
 * undefined when there's no projectCase or no long name, so a tooltip isn't shown. Shared by the
 * group header, the pose-explorer Structure filter options, and the Structure value cell so all
 * three read identically.
 * @param {string} [projectCase]
 * @returns {string|undefined}
 */
function structureTooltipTitle(projectCase) {
    if (!projectCase) return undefined;
    const { mapCase, targetInfo } = getMapCaseAndTargetInfo(projectCase);
    const longName = mapCase?.getLongName();
    if (!longName) return undefined;
    // Name the organism only when the structures of interest span more than one -- then it
    // disambiguates (each structure named, human included). A uniform set (all human, or all
    // mouse) shows nothing, so organism text appears exactly where it carries information.
    const organism = mapCase?.organism;
    const base = organism && targetsHaveMixedOrganisms() ? `${longName} — ${organism}` : longName;
    if (!targetInfo || isGroupEntry(targetInfo)) return base;
    return `${base} (${targetInfo.isTarget ? 'Target' : 'Off-Target'})`;
}
